/* Shared behaviour for the two cart surfaces: <cart-drawer> (cart-drawer.js)
   and <cart-page> (cart-page.js), which extend this class. Both sections load
   this file before their own script; the define guard below makes the second
   (deferred, duplicate) execution a no-op. */
(function () {
  window.Allvar = window.Allvar || {};
  if (window.Allvar.CartSurface) return;

  class CartSurface extends HTMLElement {
    /* Surface hooks — every subclass provides:
         regionAttribute()      attribute marking swappable regions
         statusRegion()         polite live-region element
         errorRegion()          alert live-region element
         refresh(options)       re-render from the server
       and may override:
         refreshOptionsAfterAdd(added)  options for the refresh after an add */

    refreshOptionsAfterAdd() {
      return {};
    }

    // Bound ONCE on the element itself. Handlers delegate via closest() so
    // they survive the region swaps in refresh() — the custom element is never
    // replaced, so re-binding would stack duplicates.
    bindCartLineListeners() {
      this.addEventListener('click', (event) => {
        const removeControl = event.target.closest('[data-line-remove]');
        if (removeControl) {
          const line = removeControl.closest('[data-cart-line]');
          if (!line) return; // no line to act on — let a real href navigate
          event.preventDefault();
          this.rememberFocus(line, '[data-line-remove]');
          this.changeLine(line.dataset.lineKey, 0, { line: line });
          return;
        }

        const stepBtn = event.target.closest('[data-quantity-increase], [data-quantity-decrease]');
        if (stepBtn) {
          const line = stepBtn.closest('[data-cart-line]');
          const input = line && line.querySelector('[data-quantity-value]');
          if (!line || !input) return; // stepper outside a cart line (no-JS path)
          const current = parseInt(input.value, 10) || 1;
          const next = stepBtn.hasAttribute('data-quantity-increase')
            ? current + 1
            : Math.max(0, current - 1);
          this.rememberFocus(
            line,
            stepBtn.hasAttribute('data-quantity-increase')
              ? '[data-quantity-increase]'
              : '[data-quantity-decrease]'
          );
          this.changeLine(line.dataset.lineKey, next, { line: line });
          return;
        }

        const crossAdd = event.target.closest(
          '[data-cross-sell-add] button, [data-cross-sell-add] [type="submit"]'
        );
        if (crossAdd) {
          event.preventDefault();
          this.handleCrossSellAdd(crossAdd.closest('[data-cross-sell-add]'));
        }
      });

      // Typed quantity commits on change; the dimmed busy state carries the
      // feedback since there is no button to spin.
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quantity-value]');
        if (!input) return;
        const line = input.closest('[data-cart-line]');
        if (!line) return;
        this.rememberFocus(line, '[data-quantity-value]');
        this.changeLine(line.dataset.lineKey, parseInt(input.value, 10), { line: line });
      });

      // Guard native submits on the cross-sell forms (Enter key etc.).
      this.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-cross-sell-add]');
        if (!form) return;
        event.preventDefault();
        this.handleCrossSellAdd(form);
      });
    }

    // A cross-sell with multiple variants must not silently add its first
    // variant — open the shared picker sheet so the shopper chooses
    // size/colour, mirroring the PDP sticky-bar behaviour. Single-variant
    // items add straight away.
    handleCrossSellAdd(form) {
      if (!form) return;
      const variantCount = parseInt(form.dataset.variantCount || '1', 10) || 1;
      const handle = form.dataset.productHandle;
      const sheet = document.querySelector('[data-variant-sheet]');
      if (variantCount > 1 && handle && sheet && typeof sheet.forProduct === 'function') {
        // The sheet shows its own loading state while it fetches the form; the
        // button here isn't waiting on anything, so it must not spin.
        sheet.forProduct(handle, form.dataset.productTitle);
        return;
      }
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) this.addVariant(idInput.value, form.querySelector('[type="submit"], button'));
    }

    async addVariant(id, control) {
      if (this.busy) return;
      this.setBusy(true);
      if (control) {
        control.classList.add('is-loading');
        control.disabled = true;
      }
      if (this.dataset.addingLabel) this.announceStatus(this.dataset.addingLabel);
      try {
        const added = await this.postCart('/cart/add.js', { id: id, quantity: 1 });
        if (added) await this.refresh(this.refreshOptionsAfterAdd(added));
      } finally {
        // refresh() re-renders the cross-sell region on success, so this button
        // is usually gone by now; clearing still matters when the add failed.
        if (control && control.isConnected) {
          control.classList.remove('is-loading');
          control.disabled = false;
        }
        this.setBusy(false);
      }
    }

    async changeLine(key, quantity, source) {
      if (this.busy) return; // ignore rapid repeat clicks mid-request
      this.setBusy(true);
      this.setLineBusy(source, true);
      try {
        const changed = await this.postCart('/cart/change.js', { id: key, quantity: quantity });
        if (changed) await this.refresh();
      } finally {
        // On success refresh() has already replaced the line markup, so this
        // only matters for the error path — but it must run either way, or a
        // failed update would leave the row dimmed and unusable.
        this.setLineBusy(source, false);
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.toggleAttribute('aria-busy', busy);
    }

    // Mark just the line being updated: quantity steps and removal affect one
    // row, so dimming the whole surface would overstate what is happening and
    // block the shopper from reading the rest of the cart. The activated
    // control is deliberately NOT marked .is-loading: that class hides the
    // control's own icon (see the shared rule in critical.css), which blanked
    // the stepper's "+" mid-update.
    setLineBusy(source, busy) {
      if (!source || !source.line) return;
      source.line.classList.toggle('is-busy', busy);
      source.line.toggleAttribute('aria-busy', busy);
      if (busy && this.dataset.updatingLabel) this.announceStatus(this.dataset.updatingLabel);
    }

    async postCart(url, body) {
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error((data && (data.description || data.message)) || this.dataset.errorMessage);
        }
        return data;
      } catch (e) {
        this.announceError(e && e.message);
        return null;
      }
    }

    rememberFocus(line, selector) {
      this.pendingFocus = {
        lineKey: line && line.dataset.lineKey,
        selector: selector,
      };
    }

    // Swap only the marked content regions from a freshly fetched render, so
    // the element, its listeners and any entry animation survive the refresh.
    swapRegions(fresh) {
      const attr = this.regionAttribute();
      this.querySelectorAll(`[${attr}]`).forEach((region) => {
        const next = fresh.querySelector(`[${attr}="${region.getAttribute(attr)}"]`);
        if (!next) return;
        // Hand the outgoing progress fill's width to the incoming one so it
        // animates up to the new value rather than snapping. Measured before
        // the swap while the old node still exists; committed after, once the
        // new one is in the document and can be laid out.
        const commitProgress =
          window.Allvar && window.Allvar.carryProgress
            ? window.Allvar.carryProgress(next, region)
            : null;
        region.replaceWith(next);
        if (commitProgress) commitProgress();
      });
    }

    // In-flight progress for screen readers. Uses the surface's polite live
    // region; the post-request announce then supersedes it with the resulting
    // cart state.
    announceStatus(message) {
      const live = this.statusRegion();
      if (live && message) live.textContent = message;
    }

    announceError(message) {
      const live = this.errorRegion();
      if (!live) return;
      live.textContent = '';
      window.setTimeout(() => {
        live.textContent = message || this.dataset.errorMessage || '';
      }, 50);
    }
  }

  window.Allvar.CartSurface = CartSurface;
})();
